import { ICounterData, initialCounterData } from "@home-health-notify/domain";
import { useMediaQuery } from "@mui/material";
import { OrganizationContext } from "hhn-commons";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router";
import { RESPONSIVE_DRAWER } from "../../common/Common.style";
import { RightDrawerContext } from "../../context/RightDrawerContext";
import { ROUTES } from "../../routes";
import { ChatlistPanelContainer, LeftPanelBox } from "./LeftPanel.style";
import { LeftPanelCompact } from "./LeftPanelCompact";
import { LeftPanelTab } from "./LeftPanelTab";

const CHATS_TAB = 0;
const DIRECTORY_TAB = 1;
const ALERTS_TAB = 2;

const useCounters = () => {
  const { selectedOrgUser } = useContext(OrganizationContext);
  const [counters, setCounters] = useState<ICounterData>(initialCounterData());

  useEffect(() => {
    const removeCountersListener = selectedOrgUser?.countersListeners?.(
      (next: ICounterData) => {
        //TODO: Reduce this to 1 call
        setTimeout(() => {
          setCounters({ ...next });
        }, 100);
      },
    );
    return () => {
      removeCountersListener?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return counters;
};

// Single render tree for both wide and narrow modes .
// LeftPanelTab is always mounted in the same DOM location regardless of
// viewport so mount-time fetches (e.g. /star-mark) fire exactly once, not on
// every resize across the 1280 boundary. The rail (LeftPanelCompact) and
// LeftPanelBox widths handle the visual difference between the two modes.
const LeftPanel = () => {
  const counters = useCounters();
  const isFullPanel = useMediaQuery(
    `(min-width: ${RESPONSIVE_DRAWER.WIDE_BP}px)`,
  );

  const params = useParams();
  const { open: infoOpen, closeDrawer } = useContext(RightDrawerContext);

  // URL-derived initial active tab. Subsequent rail clicks override this.
  const currentPath = params["*"]?.split("/")[0];
  const urlTab =
    currentPath === ROUTES.DIRECTORY
      ? DIRECTORY_TAB
      : currentPath === ROUTES.ALERT
        ? ALERTS_TAB
        : CHATS_TAB;

  const [pickedTab, setPickedTab] = useState<number | null>(null);
  const activeTab = pickedTab !== null ? pickedTab : urlTab;

  // Clear the user's manual override when the URL section or org changes, so
  // external navigation (deep link, org switch, clicking a chat from Directory)
  // takes over instead of being shadowed by a stale picked tab.
  useEffect(() => {
    setPickedTab(null);
  }, [params.orgId, urlTab]);

  // Chatlist is open at wide widths always; at narrow it's mutually exclusive
  // with chat info — visible iff info is closed. No explicit close handle in
  // the UI: opening info hides it, closing info auto-reopens it.
  const chatlistOpen = isFullPanel || !infoOpen;

  const handleRailClick = (tabIndex: number) => {
    setPickedTab(tabIndex);
    // Rail is only visible while info is open; clicking it always closes
    // info, which implicitly reopens the chatlist via the derived state.
    if (infoOpen) closeDrawer();
  };

  return (
    <LeftPanelBox $chatlistOpen={chatlistOpen}>
      {!isFullPanel && (
        <LeftPanelCompact
          counters={counters}
          activeTab={activeTab}
          onTabClick={handleRailClick}
          collapsed={chatlistOpen}
        />
      )}
      {/* Always mounted; clipped by LeftPanelBox `overflow: hidden` at narrow
          widths when `chatlistOpen` is false. Keeps StarredMessages and
          similar mount-time fetches from re-firing on toggle / resize. */}
      <ChatlistPanelContainer
        data-testid="chatlist-panel"
        aria-hidden={!isFullPanel && !chatlistOpen}
      >
        <LeftPanelTab
          counters={counters}
          initialTabIndex={isFullPanel ? undefined : activeTab}
          onTabChange={setPickedTab}
        />
      </ChatlistPanelContainer>
    </LeftPanelBox>
  );
};

export default LeftPanel;
